import React, { useState } from 'react';
import { useTournament } from '../hooks/useTournament';
import '../styles/MatchSchedule.css';

const MatchSchedule = () => {
  const {
    matchSchedule,
    updateScore,
    getTeamName,
    getTeamPlayers,
    teamColors,
    completedRounds
  } = useTournament();
  
  const handleScoreChange = (fieldIdx, team, value, roundIdx) => {
    const scoreValue = value === '' ? null : parseInt(value, 10);
    if (team === 'A') {
      updateScore(roundIdx, fieldIdx, scoreValue, matchSchedule[roundIdx][fieldIdx].scoreB);
    } else {
      updateScore(roundIdx, fieldIdx, matchSchedule[roundIdx][fieldIdx].scoreA, scoreValue);
    }
  };
  // Format player list for tooltip display
  const formatPlayerList = (players) => {
    if (!players || players.length === 0) return '';
    return players.join(', ');
  };

  // Handler to generate print-ready schedule for a specific field
  const handleFieldSchedule = (fieldNumber) => {
    // Build schedule data for the field
    const fieldSchedule = matchSchedule.map((round, roundIdx) => {
      const match = round[fieldNumber - 1]; // fieldNumber is 1-based, array is 0-based
      if (!match) return null;
      
      const teamAPlayers = getTeamPlayers(roundIdx, match.teamA);
      const teamBPlayers = getTeamPlayers(roundIdx, match.teamB);
      
      return {
        round: roundIdx + 1,
        teamA: getTeamName(match.teamA),
        teamB: getTeamName(match.teamB),
        teamAPlayers: teamAPlayers,
        teamBPlayers: teamBPlayers,
        scoreA: match.scoreA,
        scoreB: match.scoreB
      };
    }).filter(item => item !== null);

    // Determine header labels and colors dynamically based on first non-empty scheduled match
    let headerTeamALabel = 'Team A';
    let headerTeamBLabel = 'Team B';
    let teamAIndex = null;
    let teamBIndex = null;
    if (fieldSchedule.length > 0) {
      // Use the teams from the first round on this field
      const firstMatch = matchSchedule[0]?.[fieldNumber - 1];
      if (firstMatch) {
        teamAIndex = firstMatch.teamA;
        teamBIndex = firstMatch.teamB;
        headerTeamALabel = getTeamName(teamAIndex);
        headerTeamBLabel = getTeamName(teamBIndex);
      }
    }
    const teamAColor = teamColors?.[teamAIndex] || '#f0f0f0';
    const teamBColor = teamColors?.[teamBIndex] || '#f0f0f0';

    // Generate HTML for print
    const printWindow = window.open('', '_blank');
    const html = `
      <html>
        <head>
          <title>Feld ${fieldNumber} Spielplan</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { text-align: center; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; }
            .team-section { text-align: left; padding: 4px; }
            .team-name { font-weight: bold; font-size: 1.1em; margin-bottom: 4px; }
            .players-list { font-size: 0.9em; color: #555; }
            .score-cell { font-weight: bold; font-size: 1.2em; }
            tbody tr:nth-child(odd) { background-color: #ffffff; }
            tbody tr:nth-child(even) { background-color: #f2f6fa; }
          </style>
        </head>
        <body>
          <h1>Feld ${fieldNumber} Spielplan</h1>
          <table>
            <thead>
              <tr>
                <th>Runde</th>
                <th style="background-color: ${teamAColor}; color: ${teamAColor !== '#f0f0f0' ? '#fff' : '#333'};">${headerTeamALabel}</th>
                <th style="background-color: ${teamBColor}; color: ${teamBColor !== '#f0f0f0' ? '#fff' : '#333'};">${headerTeamBLabel}</th>
                <th>Ergebnis</th>
              </tr>
            </thead>
            <tbody>
              ${fieldSchedule.map(item => `
                <tr>
                  <td>${item.round}</td>
                  <td class="team-section">
                    <div class="players-list">${item.teamAPlayers.map(p => `<div>${p}</div>`).join('')}</div>
                  </td>
                  <td class="team-section">
                    <div class="players-list">${item.teamBPlayers.map(p => `<div>${p}</div>`).join('')}</div>
                  </td>
                  <td class="score-cell">
                    ${item.scoreA !== null && item.scoreB !== null ? `${item.scoreA} : ${item.scoreB}` : '-'}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    // printWindow.close(); // optional
  };

  if (!matchSchedule || matchSchedule.length === 0) {
    return null;
  }
  return (
    <div className="section">
      <div className="section-header">
        <h2>Spielplan</h2>
      </div>

      <p className="info-text" style={{ marginBottom: '1rem' }}>
        Auf das Feld klicken, um Spielplan für das Feld auszudrucken.
      </p>

      {/* Matrix display of all rounds */}
      <div className="matches-matrix-container">
  <table className="tournament-table match-schedule-table striped-table">
          <thead>
            <tr>
              <th className="field-header">Feld</th>
              {matchSchedule.map((_, roundIdx) => (
                <th
                  key={roundIdx}
                  className={`round-header ${completedRounds[roundIdx] ? 'round-completed' : ''}`}
                >
                  Runde {roundIdx + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matchSchedule[0].map((_, fieldIdx) => (
              <tr key={fieldIdx}>
                <td 
                  className="field-cell clickable" 
                  onClick={() => handleFieldSchedule(fieldIdx + 1)}
                  title="Klicken, um Spielplan für dieses Feld auszudrucken"
                >
                  Feld {fieldIdx + 1}
                </td>
                {matchSchedule.map((round, roundIdx) => {
                  const match = round[fieldIdx];
                  
                  // Get players for both teams
                  const teamAPlayers = getTeamPlayers(roundIdx, match.teamA);
                  const teamBPlayers = getTeamPlayers(roundIdx, match.teamB);
                  
                  return (
                    <td key={roundIdx} className={`match-cell-container ${completedRounds[roundIdx] ? 'round-completed' : ''}`}>
                      <div className="match-cell">
                        <div className="match-teams-container">
                          <div className="team-container">
                            <span
                              className="team-label team-hover"
                              title={`Team ${getTeamName(match.teamA)}: ${formatPlayerList(teamAPlayers)}`}
                              style={teamColors?.[match.teamA] ? { backgroundColor: teamColors[match.teamA], color: '#fff' } : undefined}
                            >
                              {getTeamName(match.teamA)}
                            </span>
                          </div>
                          <span className="vs">vs</span>
                          <div className="team-container">
                            <span
                              className="team-label team-hover"
                              title={`Team ${getTeamName(match.teamB)}: ${formatPlayerList(teamBPlayers)}`}
                              style={teamColors?.[match.teamB] ? { backgroundColor: teamColors[match.teamB], color: '#fff' } : undefined}
                            >
                              {getTeamName(match.teamB)}
                            </span>
                          </div>
                        </div>
                        <div className="score-display">
                          <input
                            type="number"
                            className="score-input score-input-a"
                            min="0"
                            value={match.scoreA !== null ? match.scoreA : ''}
                            onChange={(e) => handleScoreChange(fieldIdx, `A`, e.target.value, roundIdx)}
                          />
                          <span className="score-separator">:</span>
                          <input
                            type="number"
                            className="score-input score-input-b"
                            min="0"
                            value={match.scoreB !== null ? match.scoreB : ''}
                            onChange={(e) => handleScoreChange(fieldIdx, `B`, e.target.value, roundIdx)}
                          />
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MatchSchedule;